import { useCallback } from 'react'
import { pickBy, identity, isEmpty, at } from 'lodash'
import { formKeyToCommodity } from 'transfix-form/forms/CarrierLoadPreferences/components/ExcludedCommoditiesModal/constants'
import { TExcludedCommoditiesSchema } from 'transfix-form/forms/CarrierLoadPreferences/components/ExcludedCommoditiesModal/schema/ExcludedCommoditiesSchema'
import { IUseCarrierLoadPreferencesSubmitter } from 'transfix-form/forms/CarrierLoadPreferences/types'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { useCarrierProfileQuery } from '../../../../Carrier/graphql/CarrierProfile'
import { TOAST_ERROR_MESSAGE } from '../../../constants'
import { useUpdateCarrierExcludedCommoditiesMutation } from '../graphql/UpdateCarrierExcludedCommoditesMutation'

const TOAST_SUCCESS_MESSAGE = 'Excluded commodities updated successfully'

export const useExcludedCommodotiesSubmitter = (
  carrierApplicationId: string
): IUseCarrierLoadPreferencesSubmitter<TExcludedCommoditiesSchema> => {
  const { openToast } = useGlobalToast()

  const {
    loading: fetchCarrierProfileLoading,
    refetch: fetchCarrierProfile,
  } = useCarrierProfileQuery({
    skip: true,
    variables: {
      id: carrierApplicationId,
    },
  })
  const [
    updateExcludedCommodities,
    { loading: updateExcludedCommoditiesLoading },
  ] = useUpdateCarrierExcludedCommoditiesMutation()

  const showErrorToast = useCallback(() => {
    openToast({ toastMessage: TOAST_ERROR_MESSAGE, toastType: 'alert' })
  }, [openToast])

  const submitHandler = useCallback(
    async (formValues: TExcludedCommoditiesSchema) => {
      // Get list of keys that have a value of `true`.
      const formKeys = Object.keys(pickBy(formValues, identity))
      const excludedCommodities = at(formKeyToCommodity, formKeys)

      const { data, errors: gqlErrors } = await updateExcludedCommodities({
        variables: {
          input: {
            carrierApplicationId,
            excludedCommodities,
          },
        },
      })
      const { updateCarrierExcludedCommodities } = data ?? {}
      const { errors: clientErrors } = updateCarrierExcludedCommodities ?? {}

      if (isEmpty(gqlErrors) && isEmpty(clientErrors)) {
        await fetchCarrierProfile()
        openToast({ toastMessage: TOAST_SUCCESS_MESSAGE })
        return true
      }

      showErrorToast()
      return false
    },
    [
      carrierApplicationId,
      fetchCarrierProfile,
      openToast,
      showErrorToast,
      updateExcludedCommodities,
    ]
  )

  return {
    loading: fetchCarrierProfileLoading || updateExcludedCommoditiesLoading,
    submitHandler,
  }
}
